// src/features/messages/PersonPicker.tsx — shared @handle search-and-pick UI for
// NewDmDialog / CreateGroupDialog. Reuses the same data + filter Explore's People
// tab uses (usersService.suggested() + filterPeople) rather than standing up a
// second search surface; falls back to an exact usersService.getByHandle() lookup
// when the query looks like a full handle that the suggested list doesn't cover
// (a signed-up account with no follower overlap, say) — this is what lets the
// compose flow genuinely "find users by @handle" rather than only ones already
// surfaced elsewhere.
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Avatar, TextField } from '@/ui';
import { usersService } from '@/core/services/usersService';
import { filterPeople } from '@/features/explore/filterExplore';
import type { SuggestedProfile } from '@/core/models/account';

export interface PersonOption {
  handle: string;
  name: string;
  avatarKey?: string;
  verified?: boolean;
}

const Wrap = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const Results = styled.ul`
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px;
  max-height: 220px; overflow-y: auto;
`;
const Row = styled.li`display: block;`;
const RowButton = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
  padding: 8px 10px; border: none; border-radius: ${({ theme }) => theme.radius.md};
  background: transparent; cursor: pointer; color: inherit;
  &:hover, &:focus-visible { background: ${({ theme }) => theme.color.hover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Info = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px;`;
const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600; color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Empty = styled.p`
  margin: 4px 2px 0; font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};
`;
const FieldError = styled.p`
  margin: 4px 2px 0; font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.danger};
`;

function toOption(p: SuggestedProfile): PersonOption {
  return { handle: p.handle, name: p.name, avatarKey: p.av, verified: p.verified };
}

/** Debounce a keystroke burst before firing the suggested-list fetch + the
 *  getByHandle fallback — without this, every keystroke fires both. */
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Query the suggested-people list client-side, plus an exact-handle fallback.
 * Returns the RAW matches for `query` — exclusion (already-selected chips,
 * the viewer's own handle) is applied by the caller on every render, not
 * baked in here, so picking a person drops their row immediately without
 * waiting on a refetch.
 *
 * `loading` is derived by comparing `settledQuery` (which query the async
 * lookup last resolved for) against the current query — the same technique
 * as useUserByHandle in hooks/useUser.ts — rather than a separate setState
 * called synchronously inside the effect body (react-hooks/set-state-in-effect).
 * The whole fetch is one .then/.finally chain (not a bare async IIFE) so a
 * throw between the two awaits still reaches `.finally` and settles
 * `settledQuery` — otherwise `loading` could stick true forever, the exact
 * failure mode useUserByHandle's own comment calls out.
 */
function usePersonSearch(query: string) {
  const [results, setResults] = useState<PersonOption[]>([]);
  const [settledQuery, setSettledQuery] = useState('');

  useEffect(() => {
    let alive = true;
    const q = query.trim();
    if (q === '') {
      // Async, not synchronous — avoids the react-hooks/set-state-in-effect
      // rule (mirrors the same empty-query reset in hooks/useUser.ts).
      const t = setTimeout(() => {
        if (!alive) return;
        setResults([]);
        setSettledQuery('');
      }, 0);
      return () => {
        alive = false;
        clearTimeout(t);
      };
    }
    const debounce = setTimeout(() => {
      void usersService
        .suggested()
        .catch(() => [] as SuggestedProfile[])
        .then(async (suggested) => {
          const direct = filterPeople(suggested, q).map(toOption);
          if (direct.length > 0) return direct;
          const user = await usersService.getByHandle(q).catch(() => null);
          return user
            ? [{ handle: user.handle, name: user.name, avatarKey: user.avatarKey, verified: user.verified }]
            : [];
        })
        .then((matches) => {
          if (alive) setResults(matches);
        })
        .finally(() => {
          if (alive) setSettledQuery(q);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(debounce);
    };
  }, [query]);

  return { results, loading: query.trim() !== '' && settledQuery !== query.trim() };
}

function normalize(handle: string): string {
  return handle.replace(/^@/, '').toLowerCase();
}

export interface PersonPickerProps {
  /** Handles to exclude from results (already selected, or the viewer's own). */
  exclude?: string[];
  onPick: (person: PersonOption) => void;
  /** Shown under the input when the caller's last pick attempt failed. */
  error?: string | null;
  /** Called whenever the query text changes — callers use this to clear a
   *  stale `error` (e.g. a not_found for a since-edited handle) rather than
   *  leaving it displayed against a query that no longer produced it. */
  onQueryChange?: () => void;
  disabled?: boolean;
}

export function PersonPicker({ exclude = [], onPick, error, onQueryChange, disabled }: PersonPickerProps) {
  const [query, setQuery] = useState('');
  const { results: raw, loading } = usePersonSearch(query);
  const excludeSet = new Set(exclude.map(normalize));
  const results = raw.filter((p) => !excludeSet.has(normalize(p.handle)));

  return (
    <Wrap>
      <TextField
        label="Search by name or @handle"
        placeholder="@handle"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onQueryChange?.();
        }}
        disabled={disabled}
        autoComplete="off"
      />
      {error && <FieldError role="alert">{error}</FieldError>}
      {query.trim() !== '' && !loading && (
        results.length === 0 ? (
          <Empty>No one found for &ldquo;{query.trim()}&rdquo;.</Empty>
        ) : (
          <Results>
            {results.map((p) => (
              <Row key={p.handle}>
                <RowButton
                  type="button"
                  onClick={() => onPick(p)}
                  disabled={disabled}
                  aria-label={`${p.name} ${p.handle}`}
                >
                  <Avatar name={p.name} size={32} avatarKey={p.avatarKey} />
                  <Info>
                    <Name>{p.name}</Name>
                    <Handle>{p.handle}</Handle>
                  </Info>
                </RowButton>
              </Row>
            ))}
          </Results>
        )
      )}
    </Wrap>
  );
}
